import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { InputType, Source } from "@re/shared";
import { api } from "../lib/api";
import { Markdown } from "../components/Markdown";
import { ArtifactRegistry } from "../components/research/ArtifactRegistry";
import { Badge, cn, fmtDate, Icon, SourceBadge, SourceIcon, VisibilityPill } from "../components/ui";

export const Route = createFileRoute("/sessions/$slug")({
  component: SessionPage,
});

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

function SessionPage() {
  const { slug } = Route.useParams();
  const { t, i18n } = useTranslation();
  const q = useQuery({ queryKey: ["session", slug], queryFn: () => api.getReport(slug) });
  const manifest = useQuery({
    queryKey: ["session-manifest", slug],
    queryFn: () => api.getSessionManifest(slug),
    retry: false,
  });
  const [active, setActive] = useState<string | null>(null);

  const toc = useMemo(() => {
    if (!q.data) return [];
    const seen = new Map<string, number>();
    return q.data.blocks
      .filter((b) => b.level === 2)
      .map((b) => {
        const base = slugify(b.heading) || "sec";
        const n = (seen.get(base) ?? 0) + 1;
        seen.set(base, n);
        const id = n === 1 ? base : `${base}-${n}`;
        return { id, heading: b.heading };
      });
  }, [q.data]);

  useEffect(() => {
    if (toc.length === 0) return;
    const handler = () => {
      let current = toc[0]?.id ?? null;
      for (const item of toc) {
        const el = document.getElementById(`sec-${item.id}`);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.top < 120) current = item.id;
      }
      setActive(current);
    };
    window.addEventListener("scroll", handler, { passive: true });
    handler();
    return () => window.removeEventListener("scroll", handler);
  }, [toc]);

  if (q.isLoading) return <p className="py-16 text-center text-ink-faint">{t("common.loading")}</p>;
  if (q.error) return <p className="py-16 text-center text-danger">{t("session.loadFailed", { error: String(q.error) })}</p>;
  if (!q.data) return <p className="py-16 text-center text-ink-faint">{t("session.notFound")}</p>;

  const r = q.data;
  const wordCount = countWords([r.intro, ...r.blocks.map((b) => b.body)].join(" "));
  const sourceCount = r.source_count ?? r.sources.length;
  // Re-compute id-per-block so anchors match.
  const seenIds = new Map<string, number>();
  const blockId = (heading: string): string => {
    const base = slugify(heading) || "sec";
    const n = (seenIds.get(base) ?? 0) + 1;
    seenIds.set(base, n);
    return n === 1 ? base : `${base}-${n}`;
  };

  return (
    <main className="max-w-[1320px] mx-auto px-6 pb-24">
      <div className="pt-6 pb-3 flex items-center gap-2 text-[12.5px] text-ink-faint">
        <Link to="/" className="hover:text-ink inline-flex items-center gap-1">
          <Icon name="arrow-left" size={12} />
          {t("session.breadcrumb")}
        </Link>
        <span>/</span>
        <span className="font-mono truncate">sessions / {slug}</span>
      </div>

      <header className="pt-2 pb-8 max-w-[760px]">
        <div className="flex flex-wrap items-center gap-2 mb-5">
          <SourceBadge type={r.input_type} />
          {r.visibility && <VisibilityPill visibility={r.visibility} />}
          {r.model && <Badge tone="line" size="sm">{r.model}</Badge>}
          {r.intent_mode && <Badge tone="line" size="sm">{r.intent_mode}</Badge>}
        </div>
        <h1 className="font-serif text-[44px] leading-[1.06] font-medium text-ink tracking-tight">{r.title}</h1>
        <div className="mt-5 text-[12.5px] text-ink-faint font-mono">
          {fmtDate(r.created, i18n.language)} · {t("session.sourcesCount", { count: sourceCount })}
        </div>
        {r.input && <p className="mt-3 truncate text-[13px] text-ink-dim font-mono">{r.input}</p>}

        {r.intent?.purpose && (
          <div className="mt-6 rounded-lg border border-border bg-surface-2 p-4">
            <div className="text-[11px] uppercase text-ink-faint font-medium mb-1" style={{ letterSpacing: "0.08em" }}>
              {t("session.intentPurpose")}
            </div>
            <p className="text-[14px] text-ink leading-snug">{r.intent.purpose}</p>
            {r.intent.focus && <p className="mt-2 text-[12.5px] text-ink-dim">{t("session.intentFocus")}: {r.intent.focus}</p>}
            {r.intent.audience_level && <p className="text-[12.5px] text-ink-dim">{t("session.intentBackground")}: {r.intent.audience_level}</p>}
          </div>
        )}
      </header>

      <div className="flex items-center gap-1 mb-6 border-b border-border overflow-x-auto">
        <TabLink active icon="file-text">{t("session.tabReport")}</TabLink>
        {r.input_type === "youtube" ? (
          <Link
            to="/sessions/$slug/transcript"
            params={{ slug }}
            className="relative h-10 px-4 inline-flex items-center gap-2 text-[13.5px] font-medium text-ink-faint hover:text-ink-dim"
          >
            <Icon name="list" size={14} />
            {t("session.tabTranscript")}
          </Link>
        ) : (
          <button
            type="button"
            disabled
            className="relative h-10 px-4 inline-flex items-center gap-2 text-[13.5px] font-medium text-ink-faint opacity-50 cursor-not-allowed"
          >
            <Icon name="list" size={14} />
            {t("session.tabTranscript")}
            <span className="text-[10.5px] font-mono">{t("session.transcriptHint")}</span>
          </button>
        )}
        <Link
          to="/viz/$slug"
          params={{ slug }}
          className="relative h-10 px-4 inline-flex items-center gap-2 text-[13.5px] font-medium text-ink-faint hover:text-ink-dim"
        >
          <Icon name="beaker" size={14} />
          {t("session.tabViz")}
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_240px] gap-12">
        <article className="prose-report max-w-[68ch]">
          {r.intro && <Markdown markdown={r.intro} sources={r.sources} />}

          {r.blocks.map((b, i) => {
            const id = b.level === 2 ? blockId(b.heading) : null;
            return (
              <section key={`${i}-${b.heading}`} id={id ? `sec-${id}` : undefined} className="scroll-mt-24">
                {b.level === 2 ? <h2>{b.heading}</h2> : <h3>{b.heading}</h3>}
                <Markdown markdown={b.body} sources={r.sources} />
              </section>
            );
          })}

          {r.sources.length > 0 && (
            <section id="sec-sources" className="!mt-16 scroll-mt-24">
              <h2>{t("session.sourcesTitle")}</h2>
              <div className="not-prose mt-2 grid gap-px bg-border border border-border rounded-lg overflow-hidden">
                {r.sources.map((s) => (
                  <SourceRow key={s.n} source={s} />
                ))}
              </div>
            </section>
          )}
        </article>

        <aside className="hidden lg:block">
          <div className="sticky top-[76px]">
            <div className="text-micro font-medium uppercase text-ink-faint mb-3" style={{ letterSpacing: "0.08em" }}>
              {t("session.onThisPage")}
            </div>
            <nav className="space-y-px">
              {toc.map((item) => (
                <a
                  key={item.id}
                  href={`#sec-${item.id}`}
                  onClick={() => setActive(item.id)}
                  className={cn(
                    "block text-[12.5px] py-1.5 px-3 border-l-2 transition-colors",
                    active === item.id
                      ? "border-ink text-ink font-medium"
                      : "border-border text-ink-dim hover:text-ink hover:border-border-strong",
                  )}
                >
                  {item.heading}
                </a>
              ))}
              {r.sources.length > 0 && (
                <a href="#sec-sources" className="block text-[12.5px] py-1.5 px-3 border-l-2 border-border text-ink-dim hover:text-ink hover:border-border-strong">
                  {t("session.sourcesTitle")}
                </a>
              )}
            </nav>

            <div className="mt-8 pt-6 border-t border-border space-y-3">
              {r.model && <SideMeta label={t("session.metaModel")} value={r.model} />}
              <SideMeta label={t("session.metaSources")} value={t("session.sourcesCited", { count: sourceCount })} />
              <SideMeta label={t("session.metaWords")} value={wordCount.toLocaleString(i18n.language)} />
              {r.visibility && <SideMeta label={t("session.metaVisibility")} value={r.visibility} />}
            </div>

            <div className="mt-7 pt-6 border-t border-border">
              {r.input_type === "youtube" && (
                <Link
                  to="/sessions/$slug/transcript"
                  params={{ slug }}
                  className="w-full inline-flex items-center justify-between text-[12.5px] text-ink-dim hover:text-ink py-2 group"
                >
                  <span>{t("session.viewTranscript")}</span>
                  <Icon name="arrow-up-right" size={12} className="text-ink-faint group-hover:text-ink" />
                </Link>
              )}
              <Link
                to="/viz/$slug"
                params={{ slug }}
                className="w-full inline-flex items-center justify-between text-[12.5px] text-ink-dim hover:text-ink py-2 group"
              >
                <span>{t("session.vizStudio")}</span>
                <Icon name="arrow-up-right" size={12} className="text-ink-faint group-hover:text-ink" />
              </Link>
            </div>
          </div>
        </aside>
      </div>

      {manifest.data && <ArtifactRegistry manifest={manifest.data} />}
    </main>
  );
}

function TabLink({ active, icon, children }: { active?: boolean; icon: "file-text"; children: ReactNode }) {
  return (
    <span className={cn(
      "relative h-10 px-4 inline-flex items-center gap-2 text-[13.5px] font-medium",
      active ? "text-ink" : "text-ink-faint",
    )}>
      <Icon name={icon} size={14} />
      {children}
      {active && <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-ink" />}
    </span>
  );
}

function SideMeta({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 text-[12px]">
      <span className="text-ink-faint">{label}</span>
      <span className="text-ink-dim font-mono text-right">{value}</span>
    </div>
  );
}

function SourceRow({ source }: { source: Source }) {
  const { t } = useTranslation();
  const type = normalizeSourceType(source.type);
  return (
    <div id={`source-${source.n}`} className="flex items-start gap-4 p-3.5 bg-surface transition-colors">
      <div className="font-mono text-[11px] text-ink-faint w-6 text-right pt-0.5">{source.n}</div>
      <div className="w-6 h-6 rounded-md bg-surface-2 border border-border flex items-center justify-center text-ink-dim shrink-0">
        {type ? <SourceIcon type={type} size={13} /> : <Icon name="globe" size={13} />}
      </div>
      <div className="flex-1 min-w-0">
        <a href={source.url} target="_blank" rel="noreferrer" className="text-[13.5px] font-medium text-ink leading-snug hover:text-accent-ink">
          {source.title || source.url}
        </a>
        <div className="text-[12px] text-ink-faint font-mono mt-0.5 truncate">{source.url}</div>
      </div>
      <a
        href={source.url}
        target="_blank"
        rel="noreferrer"
        aria-label={t("session.openSource")}
        className="shrink-0 h-7 px-2 rounded-md text-ink-faint hover:text-ink hover:bg-surface-2 inline-flex items-center gap-1 text-[12px]"
      >
        <Icon name="external" size={12} />
      </a>
    </div>
  );
}

function normalizeSourceType(type: string): InputType | null {
  const known: InputType[] = ["youtube", "arxiv", "github", "huggingface", "blog", "community", "topic"];
  return known.includes(type as InputType) ? (type as InputType) : null;
}

function countWords(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}
